import {Button} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {nanoid} from 'nanoid';
import React, {useState} from 'react';
import {Controller, useFieldArray, useForm, useWatch} from 'react-hook-form';
import {IconPlus, IconTrash} from '@tabler/icons-react';
import type {
  EmployeeRequestForItem,
  ItemDefinitionSimpleResponse,
} from 'src/api/ProactApiClient';
import {FeatureFlagEnum} from 'src/api/ProactApiClient';
import {useProcessEmployeeRequestForItemsMutation} from 'src/redux/queries/itemAssignment';
import {useGetItemDefinitionListQuery} from 'src/redux/queries/itemDefinition';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {Textbox} from 'src/design/atoms/Textbox';
import type {IStepItem} from 'src/design/molecules/VerticalLinearStepper';
import {VerticalLinearStepper} from 'src/design/molecules/VerticalLinearStepper';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {itemAssignmentDocumentTitle} from 'src/app/item-tracker/itemAssignmentHelpers';
import {SidebarLinkItemTracker} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useItemTrackerBreadcrumbs} from 'src/shared/hooks/useItemTrackerBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';

interface ItemTrackerRequestRow {
  uuid: string;
  itemDefinitionId: number;
  comment: string;
}

interface ItemTrackerRequestFormValues {
  selectedItemDefinitionIds: number[];
  requests: ItemTrackerRequestRow[];
}

const ItemTrackerRequestPage: React.FC = () => {
  const {setAlert} = useAlert();
  useSetDocumentTitle(itemAssignmentDocumentTitle);

  const [isError, setIsError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState<number | undefined>();

  const {
    control,
    handleSubmit,
    formState: {isSubmitting},
  } = useForm<ItemTrackerRequestFormValues>({
    defaultValues: {selectedItemDefinitionIds: [], requests: []},
  });

  const {
    fields: requestFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'requests',
  });

  const selectedItemDefinitionIds = useWatch({
    control,
    name: 'selectedItemDefinitionIds',
  });
  const requests = useWatch({control, name: 'requests'});

  const {data: itemDefinitionData, isLoading} = useGetItemDefinitionListQuery({
    isRequestable: true,
    start: 0,
    length: 100,
  });
  const itemDefinitions: ItemDefinitionSimpleResponse[] =
    itemDefinitionData?.data ?? [];

  const [triggerProcessEmployeeRequestForItems] =
    useProcessEmployeeRequestForItemsMutation();

  const onSubmit = async (values: ItemTrackerRequestFormValues) => {
    try {
      const response = await triggerProcessEmployeeRequestForItems({
        requests: values.requests.map(
          i =>
            ({
              itemDefinitionId: i.itemDefinitionId,
              comment: i.comment,
            } as EmployeeRequestForItem)
        ),
      }).unwrap();

      setIsSubmitted(response.createdCount);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsError(true);
    }
  };

  const handleCompletedSteps = handleSubmit(onSubmit);

  const steps: IStepItem[] = [
    {
      label: 'Choose Items',
      handleIsContinueDisabled: () => selectedItemDefinitionIds.length === 0,
      component: (
        <Box>
          <p>
            This process will guide you through selecting what items you want to
            request.
          </p>
          <p>
            To continue, please choose one or more desired items from the list
            below. These options are provided by your administrator.
          </p>
          <Controller
            name="selectedItemDefinitionIds"
            control={control}
            render={({field}) => (
              <>
                {itemDefinitions.map(definition => (
                  <CheckboxRow
                    key={definition.id}
                    label={definition.name}
                    helpText={definition.description}
                    checked={field.value.includes(definition.id)}
                    onChange={(_, checked) =>
                      field.onChange(
                        checked
                          ? [...field.value, definition.id]
                          : field.value.filter(id => id !== definition.id)
                      )
                    }
                  />
                ))}
              </>
            )}
          />
        </Box>
      ),
    },
    {
      label: 'Provide Details',
      handleIsContinueDisabled: () => selectedItemDefinitionIds.length === 0,
      component: (
        <Box>
          <p>
            For each item you selected, you may optionally add a comment about
            each item to support your request. This comment will be conveyed to
            your supervisor and administrator. If you wish to request more than
            one quantity of an item, you can use the "Add Another" button for
            the respective item.
          </p>
          {selectedItemDefinitionIds.map((id, index) => {
            const definition = itemDefinitions.find(i => i.id === id);
            const rows = requestFields
              .map((row, rowIndex) => ({...row, rowIndex}))
              .filter(row => row.itemDefinitionId === id);

            return (
              <Box key={index} mb={24}>
                <p>
                  <b>{definition?.name}</b>
                </p>
                {rows.map((row, index2) => (
                  <Flex
                    key={row.id}
                    direction={{base: 'column', sm: 'row'}}
                    pb={8}
                    gap={16}>
                    <Box w={{base: '100%', sm: 800}}>
                      <Controller
                        name={`requests.${row.rowIndex}.comment`}
                        control={control}
                        render={({field}) => (
                          <Textbox
                            label={`No. ${index2 + 1} Comment`}
                            value={field.value}
                            onChangeCallback={field.onChange}
                          />
                        )}
                      />
                    </Box>
                    <Button
                      variant="outline"
                      color="black"
                      size="sm"
                      mt="lg"
                      leftSection={<IconTrash />}
                      onClick={() => remove(row.rowIndex)}>
                      Remove
                    </Button>
                  </Flex>
                ))}

                <Button
                  variant="outline"
                  color="black"
                  size="sm"
                  leftSection={<IconPlus />}
                  onClick={() =>
                    append({
                      uuid: nanoid(),
                      itemDefinitionId: id,
                      comment: '',
                    })
                  }>
                  Add Another
                </Button>
              </Box>
            );
          })}
        </Box>
      ),
    },
    {
      label: 'Confirm Request',
      component: (
        <Box>
          <p>
            You have are requesting the following items being issued to you.
          </p>
          <p>
            Are you sure you wish to continue? You can use the 'Back' button
            below to edit these details if needed.
          </p>

          <p>
            <b>Requested Items:</b>
          </p>
          <ul>
            {requests.map(item => {
              const definition = itemDefinitions.find(
                i => i.id === item.itemDefinitionId
              );
              const suffix = item.comment != '' ? ` - ${item.comment}` : '';

              return (
                <li key={item.uuid}>
                  <b>{definition?.name}</b>
                  {suffix}
                </li>
              );
            })}
          </ul>
        </Box>
      ),
    },
  ];

  const {breadcrumbs, fallbackTo} = useItemTrackerBreadcrumbs({
    mode: 'request',
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.ItemTracker}>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkItemTracker.icon}>
          Request Items
        </PageHeader.Title>
      </PageHeader>
      <Box mt={16}>
        {isLoading || isSubmitting ? (
          <TableBodyRender
            isLoading={isLoading || isSubmitting}
            hasData={true}
          />
        ) : isError ? (
          <Box>
            <p>
              Unfortunately, an error occured while submitting your request.
              Please contact your administrator for support.
            </p>
          </Box>
        ) : (
          <>
            {isSubmitted === undefined && (
              <VerticalLinearStepper
                steps={steps}
                onCompletedSteps={handleCompletedSteps}
              />
            )}
            {isSubmitted === -1 && (
              <Box>
                Your request was submitted, but an issue occured when returning
                the request status. Please verify if the request completed
                before submitting another request.
              </Box>
            )}
            {isSubmitted !== -1 && isSubmitted !== undefined && (
              <Box>
                Your request was submitted! {isSubmitted} items have been
                requested. Your supervisor will receive a notification to review
                your requests. You may safely leave this page.
              </Box>
            )}
          </>
        )}
      </Box>
    </ContentWrapper>
  );
};

export default ItemTrackerRequestPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ItemTrackerRequest(),
  element: <ItemTrackerRequestPage />,
  isPrivate: true,
});
